/**
 * SoftDeleteOrgDialog — destructive-confirm gate for the slice-3
 * soft-delete (`DELETE /api/platform/orgs/:orgId` body `{reason}`).
 *
 * Soft-delete is RECOVERABLE — it moves the org to the DELETED state
 * (stops + tombstones every runtime) and can be reversed with
 * REINSTATE from the Recycle Bin view — but it's still a destructive
 * fleet-wide action against every user in the org, so the dialog
 * forces the operator to (1) read the consequence bullets,
 * (2) type the exact `"I UNDERSTAND"` confirmation phrase (same
 * acknowledgement the FleetUpgradeAllDialog uses), and (3) provide a
 * 3-500 char audited reason. Confirm is gated on all three.
 *
 * PERMANENT DELETE (the irreversible step from DELETED → TERMINATED)
 * has its own dialog with a slug-keyed phrase (`PERMANENTLY DELETE
 * <slug>`) — see `PermanentDeleteOrgDialog`.
 */

import { useEffect, useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const REASON_MIN = 3;
const REASON_MAX = 500;
const CONFIRM_PHRASE = "I UNDERSTAND";

export interface SoftDeleteOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgName: string;
  orgSlug: string;
  onConfirm: (reason: string) => Promise<void> | void;
  busy?: boolean;
  error?: string | null;
}

export function SoftDeleteOrgDialog({
  open,
  onOpenChange,
  orgName,
  orgSlug,
  onConfirm,
  busy = false,
  error = null,
}: SoftDeleteOrgDialogProps) {
  const [reason, setReason] = useState("");
  const [typedConfirm, setTypedConfirm] = useState("");

  // Reset on (re)open so a previous attempt's text doesn't carry over.
  useEffect(() => {
    if (!open) {
      setReason("");
      setTypedConfirm("");
    }
  }, [open]);

  const trimmedReason = reason.trim();
  const reasonOk =
    trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX;
  const phraseOk = typedConfirm.trim() === CONFIRM_PHRASE;
  const canSubmit = reasonOk && phraseOk && !busy;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg space-y-4"
        data-testid="org-soft-delete-dialog"
      >
        <DialogHeader className="items-center sm:text-center">
          <DialogTitle className="flex items-center justify-center gap-2 text-2xl text-foreground">
            <AlertTriangle className="h-6 w-6 text-critical" />
            Move to Deleted
          </DialogTitle>
          <DialogDescription>
            Soft-deletes{" "}
            <strong className="font-mono text-foreground">{orgSlug}</strong>{" "}
            ({orgName}) — recoverable via REINSTATE from the Recycle
            Bin, but disruptive.
          </DialogDescription>
        </DialogHeader>

        <div
          className="rounded-md border border-critical/40 bg-critical/10 p-3 text-sm text-critical"
          data-testid="org-soft-delete-warning"
        >
          <p className="font-medium">
            This is a fleet-wide action against every user in this org.
          </p>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-xs">
            <li>
              <strong>Every</strong> runtime is stopped + tombstoned (no
              data is lost; recoverable via REINSTATE).
            </li>
            <li>
              <strong>Every</strong> user in the org loses access
              immediately and must reconnect after a reinstate.
            </li>
            <li>
              The org moves to the <strong>Recycle Bin</strong>{" "}
              (Deleted view) — no automatic cleanup timer; permanent
              deletion is a separate explicit step.
            </li>
          </ul>
        </div>

        <div className="space-y-1">
          <label
            htmlFor="org-soft-delete-confirm-input"
            className="text-xs font-medium text-muted"
          >
            Type{" "}
            <span className="font-mono font-semibold text-foreground">
              {CONFIRM_PHRASE}
            </span>{" "}
            to enable the action:
          </label>
          <input
            id="org-soft-delete-confirm-input"
            type="text"
            autoComplete="off"
            value={typedConfirm}
            onChange={(e) => setTypedConfirm(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            disabled={busy}
            data-testid="org-soft-delete-confirm-input"
            className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm text-foreground outline-none placeholder:text-faint focus:border-critical focus:ring-1 focus:ring-critical/30 disabled:opacity-60"
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor="org-soft-delete-reason"
            className="text-xs font-medium text-muted"
          >
            Reason (audited; {REASON_MIN}-{REASON_MAX} chars):
          </label>
          <textarea
            id="org-soft-delete-reason"
            data-testid="org-soft-delete-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Why is this org being moved to Deleted?"
            disabled={busy}
            className="w-full rounded-md border border-border bg-panel-2 px-3 py-2 text-sm text-foreground outline-none placeholder:text-faint focus:border-primary focus:ring-1 focus:ring-primary/30 disabled:opacity-60"
          />
        </div>

        {error && (
          <div
            className="rounded-md border border-critical/40 bg-critical/10 p-3 text-sm text-critical"
            data-testid="org-soft-delete-error"
          >
            {error}
          </div>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="org-soft-delete-cancel"
            className="rounded-md border border-border bg-panel-2 px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:bg-accent hover:text-foreground disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => void onConfirm(trimmedReason)}
            data-testid="org-soft-delete-confirm"
            className="inline-flex items-center gap-1 rounded-md bg-critical px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-critical/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {busy ? "Moving…" : "Move to Deleted"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
